// deps
import React, { ReactElement, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { PaymentMethodProps, B2CPaymentMethodData, IB2CPaymentGroup, IExpenseCodeData } from "@payment-ui/interfaces";

// widgets
import { PaymentMethodListView } from "@payment-ui/widgets/payment-method-views/list-view";

// helpers
import { canEnablePaymentMethod, getPaymentMethodDisplayName } from "@payment-ui/helpers/Payment";
import { getPayCtaMsg } from "@payment-ui/helpers/Upi";

// test-ids
import { B2C_METHOD_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface BillToCompanyProps extends PaymentMethodProps {
    methodData: B2CPaymentMethodData;
    paymentActions: IB2CPaymentGroup["paymentActions"];
    paymentAmount: string;
    paymentAmountWithoutDiscount?: string;
    onClickPaymentGroups?: (context: string, label: string) => void;
    expenseCodeData?: IExpenseCodeData;
    openBillImageWebview?: (onBillImagesUpdated?: VoidFunction) => Promise<void>;
}

const BILL_IMAGE_SOURCE = "full_payment";

function BillToCompanyPaymentMethod(props: BillToCompanyProps): ReactElement {
    const {
        methodData,
        paymentActions,
        isPaymentGroupEnabled,
        selected,
        paymentAmount,
        onClickPaymentGroups,
        expenseCodeData,
    } = props;

    const isMethodEnabled = useMemo(
        () => canEnablePaymentMethod(methodData, isPaymentGroupEnabled),
        [isPaymentGroupEnabled, methodData],
    );

    const isOnPaySetted = useRef(false);

    const payCtaMsg = useMemo(() => getPayCtaMsg(methodData, paymentAmount), [methodData, paymentAmount]);

    const onPay = useCallback(
        (extraData = {}): void => {
            if (!isMethodEnabled) {
                return;
            }

            const isBillUploadRequired = props.methodData.meta?.billUploadRequired;

            const handlePay = () => {
                paymentActions.onPay(props.methodData, {}, extraData);
            };

            const handleOnPayWithBusinessDetails = () => {
                props.openBillImageWebview?.(handlePay);
            };

            if (expenseCodeData?.showExpenseCodeHalfCard && !expenseCodeData.isBillToCompanySelected) {
                expenseCodeData.setOnSubmit(isBillUploadRequired ? handleOnPayWithBusinessDetails : handlePay);
                expenseCodeData.setIsFullPaymentMode(true);
                expenseCodeData.onOpenHalfcard();
                return;
            }

            // if user selected bill to company on cart page, then navigate to payment page
            if (expenseCodeData?.isBillToCompanySelected) {
                handlePay();
                return;
            }

            if (isBillUploadRequired) {
                paymentActions.requestForBillImage?.({
                    balance: methodData.balance,
                    onBillImagesUpdated: handlePay,
                    source: BILL_IMAGE_SOURCE,
                });
                return;
            }

            handlePay();
        },
        [isMethodEnabled, props, expenseCodeData, paymentActions, methodData.balance],
    );

    const onClick = useCallback((): void => {
        onClickPaymentGroups &&
            onClickPaymentGroups(getPaymentMethodDisplayName(props.methodData), String(isMethodEnabled));

        if (!isMethodEnabled) {
            return;
        }
        paymentActions.onSelect(props.methodData);
    }, [isMethodEnabled, paymentActions, props.methodData, onClickPaymentGroups]);

    useEffect(() => {
        if (expenseCodeData?.isBillToCompanySelected && !isOnPaySetted.current) {
            expenseCodeData?.setOnSubmit(onPay);
            isOnPaySetted.current = true;
        }
    }, [expenseCodeData, isOnPaySetted, onPay]);

    useEffectOnce(() => {
        paymentActions.onPaymentWalletLoad?.({
            walletName: methodData.name,
            orderAmount: Number(paymentAmount),
        });
    });

    return (
        <PaymentMethodListView
            methodData={props.methodData}
            isMethodEnabled={isMethodEnabled}
            onClick={onClick}
            onPayButtonClick={onPay}
            testIds={B2C_METHOD_TEST_IDS}
            selected={selected}
            payCtaMsg={payCtaMsg}
            balance={methodData.balance}
        />
    );
}

export default memo(BillToCompanyPaymentMethod);
